import { blocksToCardMarkdown } from '@shared/card-markdown'
import { type JSONContent } from '@tiptap/core'
import { DragHandle } from '@tiptap/extension-drag-handle-react'
import { type Editor } from '@tiptap/react'
import { type ComputePositionConfig } from '@floating-ui/dom'
import { clsx } from 'clsx'
import { GripVertical } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useI18n } from '@/i18n/i18n'
import { NO_DRAG } from '../classes'
import { endBlockDrag, startBlockDrag } from './blockDrag'

/**
 * Beside the block's first line, to its left. The card is drawn scaled with
 * the board's zoom; floating-ui's placement is off by that scale, so place it
 * from the card's own (unscaled) coordinates.
 */
const PLACEMENT: ComputePositionConfig = {
  placement: 'left-start',
  strategy: 'absolute',
  middleware: [
    {
      name: 'unscaled',
      fn: ({ elements }) => {
        const el = elements.floating
        const wrapper = el.parentElement
        const base = wrapper?.offsetParent
        if (!wrapper || !(base instanceof HTMLElement)) return {}
        const scale = base.getBoundingClientRect().width / base.offsetWidth || 1
        const at = wrapper.getBoundingClientRect()
        const ref = elements.reference.getBoundingClientRect()
        return {
          x: (ref.left - at.left) / scale - el.offsetWidth,
          y: (ref.top - at.top) / scale,
        }
      },
    },
  ],
}

/** The handle beside the block under the pointer; none beside the title, which stays. */
export function BlockHandle({ editor, cardId }: { editor: Editor; cardId: string }) {
  const { t } = useI18n()
  const [onTitle, setOnTitle] = useState(true)
  const armed = useRef(false)

  // After the handle's own dragstart (it selects the blocks and draws the
  // drag image), note what is being dragged, and offer it as text to places
  // outside moot.
  useEffect(() => {
    const onStart = (e: DragEvent) => {
      if (!armed.current) return
      armed.current = false
      const { from, to } = editor.state.selection
      if (from === to) return
      const blocks = editor.state.doc.slice(from, to).content.toJSON() as JSONContent[]
      startBlockDrag({ cardId, editor, from, to, blocks })
      e.dataTransfer?.setData('text/plain', blocksToCardMarkdown(blocks).replace(/^#\s*\n+/, ''))
    }
    const onEnd = () => {
      // Read by drop handlers first: a drop fires before dragend.
      setTimeout(() => {
        endBlockDrag(editor)
        // Forget the block the handle was on: coming back to the same block
        // (or the one now in its place) shows the handle again.
        if (!editor.isDestroyed)
          editor.view.dispatch(editor.state.tr.setMeta('hideDragHandle', true))
      }, 0)
    }
    document.addEventListener('dragstart', onStart)
    document.addEventListener('dragend', onEnd)
    return () => {
      document.removeEventListener('dragstart', onStart)
      document.removeEventListener('dragend', onEnd)
    }
  }, [editor, cardId])

  // Stable: the handle's plugin is set up again whenever these change.
  const onNodeChange = useCallback(({ pos }: { pos: number }) => {
    setOnTitle(pos <= 0)
  }, [])
  const onDragStart = useCallback(() => {
    armed.current = true
  }, [])

  return (
    <DragHandle
      editor={editor}
      onNodeChange={onNodeChange}
      onElementDragStart={onDragStart}
      computePositionConfig={PLACEMENT}
    >
      <div
        data-block-handle=""
        title={t('block.handle')}
        aria-label={t('block.handle')}
        className={clsx(
          NO_DRAG,
          'mt-[3px] -mr-0.5 flex h-4 w-3.5 cursor-grab items-center justify-center rounded text-muted hover:bg-surface-2 hover:text-ink active:cursor-grabbing',
          onTitle && 'invisible',
        )}
      >
        <GripVertical size={12} />
      </div>
    </DragHandle>
  )
}
